import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { format, parseISO } from 'date-fns';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import AdminLayout from '../../components/layout/AdminLayout';
import HRLayout from '../../components/layout/HRLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ScrollText,
  ClipboardList,
  FileText,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Archive,
  ArchiveRestore,
  History,
  Activity,
  GripVertical,
  Eye,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  Mail,
} from 'lucide-react';
import RichTextEditor from '../../components/legal/RichTextEditor';
import { Checkbox } from '../../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  { key: 'policy', label: 'Company Policies', pathPlural: 'policies', icon: ScrollText, noun: 'Policy' },
  { key: 'procedure', label: 'Company Procedures', pathPlural: 'procedures', icon: ClipboardList, noun: 'Procedure' },
  { key: 'terms', label: 'Terms & Conditions', pathPlural: 'terms', icon: FileText, noun: 'Terms' },
];

const sanitize = (html) => DOMPurify.sanitize(html || '', { ADD_ATTR: ['target', 'rel'] });

// ---------- Email document to staff dialog ----------
function EmailStaffDialog({ state, onClose, kind, kindMeta }) {
  const [staff, setStaff] = useState([]);
  const [allStaff, setAllStaff] = useState(true);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!state.open) return;
    setAllStaff(true); setSelected([]); setSearch('');
    axios.get(`${API}/admin/employees`)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.employees || []);
        setStaff(list.filter((u) => (u.status || 'active') === 'active' && u.email));
      })
      .catch(() => toast.error('Could not load staff list'));
  }, [state.open]);

  const toggle = (id) => setSelected((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const filtered = staff.filter((u) =>
    (u.name || '').toLowerCase().includes(search.trim().toLowerCase()));

  const send = async () => {
    if (!allStaff && selected.length === 0) {
      toast.error('Select at least one staff member or choose all staff');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/legal/${kind}/${state.doc.id}/email`, {
        all_staff: allStaff,
        recipient_ids: allStaff ? [] : selected,
      });
      toast.success(`"${state.doc.title}" emailed to ${res.data.emails_sent} staff member(s)`);
      if (res.data.emails_failed > 0) {
        toast.warning(`${res.data.emails_failed} email(s) failed to send`);
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="email-staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" /> Email {kindMeta.noun} to staff
          </DialogTitle>
          <DialogDescription>{state.doc?.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="email-all-staff-row">
            <Checkbox checked={allStaff} onCheckedChange={(v) => setAllStaff(Boolean(v))} data-testid="email-all-staff-checkbox" />
            <span className="text-sm text-foreground">Send to all active staff (employees + supervisors)</span>
          </label>
          {!allStaff && (
            <div className="space-y-2">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff by name..."
                className="h-8 text-xs rounded-sm" data-testid="email-staff-search" />
              <div className="max-h-56 overflow-y-auto border border-border rounded-sm divide-y divide-border/50">
                {filtered.length === 0 && <p className="text-xs text-muted-foreground italic p-2">No matching active staff</p>}
                {filtered.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-secondary/50"
                    data-testid={`email-staff-option-${u.id}`}>
                    <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                    <span className="text-xs text-foreground flex-1">{u.name}</span>
                    <span className="text-[10px] uppercase text-muted-foreground">{u.role}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">{selected.length} selected</p>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            The full document content is included in the email, plus a link to open it in the app.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="email-cancel-btn">Cancel</Button>
          <Button onClick={send} disabled={sending} className="rounded-sm" data-testid="email-send-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
            {sending ? 'Sending…' : allStaff ? 'Email all staff' : `Email ${selected.length} selected`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Single sortable card ----------
function SortableDocCard({ doc, onEdit, onArchive, onRestore, onDelete, onVersions, onAudit, onPreview, onEmail, readOnly = false }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: doc.id, disabled: readOnly });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };
  const status = doc.status || 'published';
  const statusColors = {
    published: 'bg-green-600/15 text-green-400 border-green-500/40',
    draft: 'bg-amber-600/15 text-amber-300 border-amber-500/40',
    archived: 'bg-slate-600/30 text-slate-300 border-slate-500/40',
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className="bg-card border-border rounded-sm"
      data-testid={`legal-card-${doc.id}`}
    >
      <CardContent className="p-3 sm:p-4 flex items-center gap-3">
        {!readOnly && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground rounded-sm"
          title="Drag to reorder"
          data-testid={`drag-${doc.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm sm:text-base font-medium text-foreground truncate">{doc.title || '(untitled)'}</h3>
            <Badge variant="outline" className={`text-[10px] border ${statusColors[status]}`}>{status.toUpperCase()}</Badge>
            <Badge variant="outline" className="text-[10px] border-border/60 text-muted-foreground">v{doc.version || 1}</Badge>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Updated {doc.updated_at ? format(parseISO(doc.updated_at), 'dd MMM yyyy HH:mm') : '—'}
            {doc.updated_by_name ? ` by ${doc.updated_by_name}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onPreview(doc)}
            data-testid={`preview-${doc.id}`}
          >
            <Eye className="w-3.5 h-3.5 mr-1" />
            Preview
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onEmail(doc)}
            title="Email this document to staff"
            data-testid={`email-${doc.id}`}
          >
            <Mail className="w-3.5 h-3.5 mr-1" />
            Email
          </Button>
          {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onEdit(doc)}
            data-testid={`edit-${doc.id}`}
          >
            <Edit className="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" data-testid={`menu-${doc.id}`}>
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onVersions(doc)} data-testid={`versions-${doc.id}`}>
                <History className="w-4 h-4 mr-2" />
                Version history
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAudit(doc)} data-testid={`audit-${doc.id}`}>
                <Activity className="w-4 h-4 mr-2" />
                Audit log
              </DropdownMenuItem>
              {!readOnly && (
              <>
              <DropdownMenuSeparator />
              {status === 'archived' ? (
                <DropdownMenuItem onClick={() => onRestore(doc)} data-testid={`restore-${doc.id}`}>
                  <ArchiveRestore className="w-4 h-4 mr-2 text-emerald-400" />
                  Restore (unarchive)
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onArchive(doc)} data-testid={`archive-${doc.id}`}>
                  <Archive className="w-4 h-4 mr-2 text-amber-300" />
                  Archive
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onDelete(doc)}
                className="text-destructive focus:text-destructive"
                data-testid={`delete-${doc.id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
              </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Per-kind list ----------
function KindManager({ kind, readOnly = false }) {
  const kindMeta = KINDS.find((k) => k.key === kind);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Editor dialog state
  const [editorState, setEditorState] = useState({ open: false, mode: 'create', doc: null });
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [changeNote, setChangeNote] = useState('');
  // When editing a published Policy/Procedure, default to notifying staff +
  // resetting acknowledgements. Terms have no acknowledgement, so this flag
  // is irrelevant for them and the UI hides it.
  const [notifyOnPublish, setNotifyOnPublish] = useState(true);
  const [saving, setSaving] = useState(false);

  // Other dialogs
  const [deleteDialog, setDeleteDialog] = useState({ open: false, doc: null });
  const [versionsDialog, setVersionsDialog] = useState({ open: false, doc: null, items: [], loading: false });
  const [auditDialog, setAuditDialog] = useState({ open: false, doc: null, items: [], loading: false });
  const [previewDialog, setPreviewDialog] = useState({ open: false, doc: null });
  const [emailDialog, setEmailDialog] = useState({ open: false, doc: null });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/${kindMeta.pathPlural}`);
      setDocs(res.data || []);
    } catch (e) {
      toast.error(`Could not load ${kindMeta.label.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [kindMeta.pathPlural, kindMeta.label]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (statusFilter !== 'all' && (d.status || 'published') !== statusFilter) return false;
      if (!term) return true;
      const hay = `${d.title || ''} ${(d.content || '').replace(/<[^>]+>/g, ' ')}`.toLowerCase();
      return hay.includes(term);
    });
  }, [docs, search, statusFilter]);

  const openCreate = () => {
    setEditorState({ open: true, mode: 'create', doc: null });
    setDraftTitle('');
    setDraftContent('');
    setChangeNote('');
    setNotifyOnPublish(true);
  };

  const openEdit = (doc) => {
    setEditorState({ open: true, mode: 'edit', doc });
    setDraftTitle(doc.title || '');
    setDraftContent(doc.content || '');
    setChangeNote('');
    setNotifyOnPublish(true);
  };

  const handleSave = async () => {
    if (!draftTitle.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editorState.mode === 'create') {
        await axios.post(`${API}/${kindMeta.pathPlural}`, {
          title: draftTitle.trim(),
          content: draftContent || '',
        });
        toast.success(`${kindMeta.noun} created`);
      } else {
        const res = await axios.put(`${API}/legal/${kind}/${editorState.doc.id}`, {
          title: draftTitle.trim(),
          content: draftContent || '',
          change_note: changeNote.trim(),
          notify_on_publish: notifyOnPublish,
        });
        const n = res.data?.notification;
        if (n && (n.emails_sent || n.recipients_total)) {
          toast.success(`${kindMeta.noun} updated · ${n.emails_sent} staff notified · acknowledgements reset`);
        } else {
          toast.success(`${kindMeta.noun} updated — new version saved`);
        }
      }
      setEditorState({ open: false, mode: 'create', doc: null });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const doc = deleteDialog.doc;
    if (!doc) return;
    try {
      await axios.delete(`${API}/${kindMeta.pathPlural}/${doc.id}`);
      toast.success(`${kindMeta.noun} deleted`);
      setDeleteDialog({ open: false, doc: null });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleArchive = async (doc) => {
    if (!window.confirm(`Archive "${doc.title}"? Staff will no longer see it.`)) return;
    try {
      await axios.post(`${API}/legal/${kind}/${doc.id}/archive`);
      toast.success(`${kindMeta.noun} archived`);
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
  };

  const handleRestore = async (doc) => {
    try {
      await axios.post(`${API}/legal/${kind}/${doc.id}/restore`);
      toast.success(`${kindMeta.noun} restored`);
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    }
  };

  const openVersions = async (doc) => {
    setVersionsDialog({ open: true, doc, items: [], loading: true });
    try {
      const res = await axios.get(`${API}/legal/${kind}/${doc.id}/versions`);
      setVersionsDialog((p) => ({ ...p, items: res.data || [], loading: false }));
    } catch (e) {
      setVersionsDialog((p) => ({ ...p, loading: false }));
      toast.error('Could not load versions');
    }
  };

  const restoreVersion = async (version) => {
    const doc = versionsDialog.doc;
    if (!doc) return;
    if (!window.confirm(`Restore "${doc.title}" to v${version.version}? A new version will be created from this snapshot.`)) return;
    try {
      await axios.post(`${API}/legal/${kind}/${doc.id}/versions/${version.id}/restore`);
      toast.success(`Restored to v${version.version} — saved as a new current version`);
      setVersionsDialog({ open: false, doc: null, items: [], loading: false });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore version failed');
    }
  };

  const openAudit = async (doc) => {
    setAuditDialog({ open: true, doc, items: [], loading: true });
    try {
      const res = await axios.get(`${API}/legal/audit?doc_id=${doc.id}&limit=200`);
      setAuditDialog((p) => ({ ...p, items: res.data || [], loading: false }));
    } catch (e) {
      setAuditDialog((p) => ({ ...p, loading: false }));
      toast.error('Could not load audit log');
    }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = docs.findIndex((d) => d.id === active.id);
    const newIndex = docs.findIndex((d) => d.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(docs, oldIndex, newIndex);
    setDocs(reordered);
    try {
      await axios.post(`${API}/legal/${kind}/reorder`, {
        items: reordered.map((d, i) => ({ id: d.id, display_order: i })),
      });
      toast.success('Order updated');
    } catch (e) {
      toast.error('Reorder failed — refreshing');
      fetchDocs();
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${kindMeta.label.toLowerCase()}…`}
            className="pl-9 bg-background border-border rounded-sm"
            data-testid={`search-${kind}`}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44 bg-background border-border rounded-sm" data-testid={`status-filter-${kind}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        {!readOnly && (
        <Button onClick={openCreate} className="bg-primary text-primary-foreground" data-testid={`add-${kind}`}>
          <Plus className="w-4 h-4 mr-2" />
          Add {kindMeta.noun}
        </Button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-sm text-muted-foreground italic">Loading…</p>
      ) : filtered.length === 0 ? (
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-10 text-center">
            <kindMeta.icon className="w-10 h-10 mx-auto text-muted-foreground mb-3 opacity-50" />
            <p className="text-sm text-muted-foreground">
              {search || statusFilter !== 'all' ? 'No documents match your filters.' : `No ${kindMeta.label.toLowerCase()} yet.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={filtered.map((d) => d.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {filtered.map((d) => (
                <SortableDocCard
                  key={d.id}
                  doc={d}
                  onEdit={openEdit}
                  onArchive={handleArchive}
                  onRestore={handleRestore}
                  onDelete={(doc) => setDeleteDialog({ open: true, doc })}
                  onVersions={openVersions}
                  onAudit={openAudit}
                  onPreview={(doc) => setPreviewDialog({ open: true, doc })}
                  onEmail={(doc) => setEmailDialog({ open: true, doc })}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Email to staff dialog */}
      <EmailStaffDialog
        state={emailDialog}
        onClose={() => setEmailDialog({ open: false, doc: null })}
        kind={kind}
        kindMeta={kindMeta}
      />

      {/* Editor dialog */}
      <Dialog
        open={editorState.open}
        onOpenChange={(open) => { if (!open) setEditorState({ open: false, mode: 'create', doc: null }); }}
      >
        <DialogContent className="bg-card border-border rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="legal-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase">
              {editorState.mode === 'create' ? `New ${kindMeta.noun}` : `Edit ${kindMeta.noun}`}
            </DialogTitle>
            <DialogDescription>
              {editorState.mode === 'edit'
                ? 'A new version is saved every time you save. Older versions remain in history and can be restored.'
                : 'Create a new document. It becomes immediately published and visible to staff.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title</Label>
              <Input
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder={`e.g. ${kindMeta.noun === 'Terms' ? 'Event Operating Terms' : 'Health & Safety'}`}
                className="bg-background border-border rounded-sm"
                data-testid="legal-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Content</Label>
              <RichTextEditor
                value={draftContent}
                onChange={setDraftContent}
                testId="legal-content-editor"
              />
            </div>
            {editorState.mode === 'edit' && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Change note (optional)</Label>
                <Input
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  placeholder="e.g. clarified scope, fixed typo, updated date"
                  className="bg-background border-border rounded-sm"
                  data-testid="legal-change-note"
                />
              </div>
            )}
            {editorState.mode === 'edit'
              && (kind === 'policy' || kind === 'procedure')
              && (editorState.doc?.status || 'published') === 'published' && (
              <label
                htmlFor="notify-on-publish"
                className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm cursor-pointer hover:bg-amber-500/15 transition-colors"
                data-testid="notify-on-publish-row"
              >
                <Checkbox
                  id="notify-on-publish"
                  checked={notifyOnPublish}
                  onCheckedChange={(v) => setNotifyOnPublish(Boolean(v))}
                  className="mt-0.5"
                  data-testid="notify-on-publish-checkbox"
                />
                <div className="space-y-1 flex-1">
                  <p className="text-sm font-medium text-amber-200 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email staff &amp; require re-acknowledgement
                  </p>
                  <p className="text-xs text-amber-100/70">
                    All previous acknowledgements for this {kindMeta.noun.toLowerCase()} will be cleared and every active employee + supervisor will receive an email asking them to read and acknowledge the new version. Uncheck only for purely cosmetic edits (typos, formatting) that don&apos;t require re-reading.
                  </p>
                </div>
              </label>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditorState({ open: false, mode: 'create', doc: null })} className="border-border rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground" data-testid="legal-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editorState.mode === 'create' ? 'Create' : 'Save as new version'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog((p) => ({ ...p, open }))}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {kindMeta.noun}?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{deleteDialog.doc?.title}&rdquo; will be permanently removed, along with its version history and any acknowledgements. Consider <strong>Archive</strong> instead to preserve the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-red-600" data-testid="legal-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Versions dialog */}
      <Dialog
        open={versionsDialog.open}
        onOpenChange={(open) => { if (!open) setVersionsDialog({ open: false, doc: null, items: [], loading: false }); }}
      >
        <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="versions-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase">Version History</DialogTitle>
            <DialogDescription>
              {versionsDialog.doc?.title} — restoring a version creates a new current version from that snapshot.
            </DialogDescription>
          </DialogHeader>
          {versionsDialog.loading ? (
            <p className="text-sm text-muted-foreground italic">Loading…</p>
          ) : versionsDialog.items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No version history yet.</p>
          ) : (
            <div className="space-y-2 mt-2">
              {versionsDialog.items.map((v) => (
                <Card key={v.id} className="bg-background border-border rounded-sm" data-testid={`version-${v.id}`}>
                  <CardContent className="p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="text-[10px] bg-primary/15 text-primary border border-primary/40">v{v.version}</Badge>
                        <span className="text-sm font-medium truncate">{v.title}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {v.saved_at ? format(parseISO(v.saved_at), 'dd MMM yyyy HH:mm') : ''}
                        {v.saved_by_name ? ` · by ${v.saved_by_name}` : ''}
                      </p>
                      {v.change_note && (
                        <p className="text-xs text-muted-foreground italic mt-1">&ldquo;{v.change_note}&rdquo;</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setPreviewDialog({ open: true, doc: { title: `${v.title} (v${v.version})`, content: v.content } })}
                        data-testid={`version-preview-${v.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Preview
                      </Button>
                      {!readOnly && (
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-primary text-primary-foreground"
                        onClick={() => restoreVersion(v)}
                        data-testid={`version-restore-${v.id}`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Restore
                      </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit dialog */}
      <Dialog
        open={auditDialog.open}
        onOpenChange={(open) => { if (!open) setAuditDialog({ open: false, doc: null, items: [], loading: false }); }}
      >
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase">Audit Log</DialogTitle>
            <DialogDescription>{auditDialog.doc?.title}</DialogDescription>
          </DialogHeader>
          {auditDialog.loading ? (
            <p className="text-sm text-muted-foreground italic">Loading…</p>
          ) : auditDialog.items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No audit events recorded yet.</p>
          ) : (
            <div className="space-y-1.5 mt-2">
              {auditDialog.items.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-2 bg-background border border-border/60 rounded-sm text-xs">
                  <Badge variant="outline" className="text-[10px] capitalize">{a.action.replace(/_/g, ' ')}</Badge>
                  <span className="flex-1 truncate text-foreground">{a.actor_name || 'system'}</span>
                  <span className="text-muted-foreground">{a.at ? format(parseISO(a.at), 'dd MMM yy HH:mm') : ''}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview dialog (renders sanitised HTML) */}
      <Dialog
        open={previewDialog.open}
        onOpenChange={(open) => { if (!open) setPreviewDialog({ open: false, doc: null }); }}
      >
        <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase">{previewDialog.doc?.title || 'Preview'}</DialogTitle>
          </DialogHeader>
          <div
            className="prose prose-invert max-w-none text-sm leading-relaxed [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:p-2 [&_a]:text-primary [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: sanitize(previewDialog.doc?.content || '') }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Top-level page with 3 tabs ----------
export default function AdminLegalManagerPage({ hrMode = false }) {
  const Layout = hrMode ? HRLayout : AdminLayout;
  const [tab, setTab] = useState('policy');

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen" data-testid="legal-manager-page">
        <div className="border-b border-border pb-4">
          <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <FileText className="w-6 h-6 text-primary" strokeWidth={1.5} />
            LEGAL &amp; POLICIES MANAGER
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {hrMode
              ? 'View policies, monitor distribution and acknowledgements, and email documents to staff. Content changes are managed by the Primary Admin.'
              : 'Author, edit, archive and version-control all staff-facing policies, procedures and terms.'}
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="bg-muted/40 border border-border rounded-sm grid grid-cols-3 max-w-2xl">
            {KINDS.map((k) => (
              <TabsTrigger key={k.key} value={k.key} className="text-xs sm:text-sm" data-testid={`tab-${k.key}`}>
                <k.icon className="w-3.5 h-3.5 mr-1.5" />
                {k.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {KINDS.map((k) => (
            <TabsContent key={k.key} value={k.key} className="space-y-3 mt-3">
              <KindManager kind={k.key} readOnly={hrMode} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
}
